'use client';
import React, { useState } from 'react';
import { ExternalLink, Edit, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import Badge from '@/components/ui/Badge';
import EmptyState from '@/components/ui/EmptyState';
import { Users } from 'lucide-react';
import { Customer } from './customerTypes';

interface CustomerTableProps {
  customers: Customer[];
  onViewCustomer: (c: Customer) => void;
}

type SortKey = 'customerNumber' | 'fullName' | 'monthlyFee' | 'monthsActive' | 'lockInMonths';

export default function CustomerTable({ customers, onViewCustomer }: CustomerTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>('customerNumber');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const perPage = 10;

  const sorted = [...customers].sort((a, b) => {
    const av = a[sortKey];
    const bv = b[sortKey];
    if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
    return sortDir === 'asc'
      ? String(av).localeCompare(String(bv))
      : String(bv).localeCompare(String(av));
  });

  const totalPages = Math.max(1, Math.ceil(sorted.length / perPage));
  const paginated = sorted.slice((page - 1) * perPage, page * perPage);

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  function SortIcon({ col }: { col: SortKey }) {
    if (sortKey !== col) return <ChevronUp size={12} className="text-muted-foreground/40" />;
    return sortDir === 'asc' ? <ChevronUp size={12} className="text-primary" /> : <ChevronDown size={12} className="text-primary" />;
  }

  const contractBadgeMap: Record<string, 'active' | 'warning' | 'inactive'> = {
    active: 'active',
    suspended: 'warning',
    expired: 'inactive',
  };

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden">
      <div className="px-5 py-3 border-b border-border bg-muted/30 flex items-center justify-between">
        <span className="text-sm font-600 text-foreground">
          {customers.length} customer{customers.length !== 1 ? 's' : ''} found
        </span>
        <span className="text-xs text-muted-foreground">
          Page {page} of {totalPages}
        </span>
      </div>

      {customers.length === 0 ? (
        <EmptyState
          icon={<Users size={28} />}
          title="No customers registered yet"
          description="Register your first customer to assign a CUST number and begin tracking their service contract, machine model, and 36-month lock-in period."
        />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[1100px]">
              <thead>
                <tr className="border-b border-border bg-muted/20">
                  {[
                    { key: 'customerNumber' as SortKey, label: 'CUST #' },
                    { key: 'fullName' as SortKey, label: 'Full Name' },
                  ].map((col) => (
                    <th
                      key={`th-${col.key}`}
                      className="text-left px-4 py-3 text-xs font-600 uppercase tracking-wide text-muted-foreground cursor-pointer hover:text-foreground select-none"
                      onClick={() => handleSort(col.key)}
                    >
                      <div className="flex items-center gap-1">
                        {col.label}
                        <SortIcon col={col.key} />
                      </div>
                    </th>
                  ))}
                  <th className="text-left px-4 py-3 text-xs font-600 uppercase tracking-wide text-muted-foreground">Mobile</th>
                  <th className="text-left px-4 py-3 text-xs font-600 uppercase tracking-wide text-muted-foreground">Address</th>
                  <th className="text-left px-4 py-3 text-xs font-600 uppercase tracking-wide text-muted-foreground">Machine Model</th>
                  <th
                    className="text-left px-4 py-3 text-xs font-600 uppercase tracking-wide text-muted-foreground cursor-pointer hover:text-foreground select-none"
                    onClick={() => handleSort('monthlyFee')}
                  >
                    <div className="flex items-center gap-1">Monthly Fee <SortIcon col="monthlyFee" /></div>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-600 uppercase tracking-wide text-muted-foreground">Contract</th>
                  <th
                    className="text-left px-4 py-3 text-xs font-600 uppercase tracking-wide text-muted-foreground cursor-pointer hover:text-foreground select-none"
                    onClick={() => handleSort('monthsActive')}
                  >
                    <div className="flex items-center gap-1">Months Active <SortIcon col="monthsActive" /></div>
                  </th>
                  <th
                    className="text-left px-4 py-3 text-xs font-600 uppercase tracking-wide text-muted-foreground cursor-pointer hover:text-foreground select-none"
                    onClick={() => handleSort('lockInMonths')}
                  >
                    <div className="flex items-center gap-1">Lock-in Left <SortIcon col="lockInMonths" /></div>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-600 uppercase tracking-wide text-muted-foreground">Technician</th>
                  <th className="text-left px-4 py-3 text-xs font-600 uppercase tracking-wide text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((c) => (
                  <tr key={c.id} className="border-b border-border hover:bg-muted/40 transition-colors group">
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs font-600 text-primary bg-secondary px-2 py-0.5 rounded">{c.customerNumber}</span>
                    </td>
                    <td className="px-4 py-3 font-600 text-foreground">{c.fullName}</td>
                    <td className="px-4 py-3 text-muted-foreground">{c.mobile}</td>
                    <td className="px-4 py-3 text-muted-foreground max-w-[160px]">
                      <span className="truncate block" title={c.address}>{c.address}</span>
                    </td>
                    <td className="px-4 py-3 text-foreground">{c.machineModel}</td>
                    <td className="px-4 py-3 font-700 text-foreground tabular-nums">₱{c.monthlyFee.toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <Badge variant={contractBadgeMap[c.contractStatus]}>
                        {c.contractStatus.charAt(0).toUpperCase() + c.contractStatus.slice(1)}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 tabular-nums text-foreground">{c.monthsActive} mo</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-16 bg-muted rounded-full h-1.5 overflow-hidden">
                          <div
                            className="h-full bg-primary rounded-full"
                            style={{ width: `${Math.min(100, (c.lockInMonths / 36) * 100)}%` }}
                          />
                        </div>
                        <span className="text-xs tabular-nums text-muted-foreground">{36 - c.lockInMonths}mo left</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">{c.assignedTechnician || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => onViewCustomer(c)}
                          className="p-1.5 rounded hover:bg-secondary text-primary transition-colors"
                          title="View customer profile"
                          aria-label="View customer profile"
                        >
                          <ExternalLink size={14} />
                        </button>
                        <button
                          className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                          title="Edit customer"
                          aria-label="Edit customer"
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          className="p-1.5 rounded hover:bg-red-50 text-muted-foreground hover:text-danger transition-colors"
                          title="Delete customer — this cannot be undone"
                          aria-label="Delete customer"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-5 py-3 border-t border-border bg-muted/20">
            <span className="text-xs text-muted-foreground">
              Showing {Math.min((page - 1) * perPage + 1, customers.length)}–{Math.min(page * perPage, customers.length)} of {customers.length}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-2.5 py-1.5 text-xs font-600 bg-card border border-border rounded hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Prev
              </button>
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={`page-${i + 1}`}
                  onClick={() => setPage(i + 1)}
                  className={`w-7 h-7 text-xs font-600 rounded transition-colors ${
                    page === i + 1 ? 'bg-primary text-primary-foreground' : 'bg-card border border-border hover:bg-muted'
                  }`}
                >
                  {i + 1}
                </button>
              ))}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-2.5 py-1.5 text-xs font-600 bg-card border border-border rounded hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}